import React, { useState, useEffect } from 'react'
import { useChannelStateContext } from 'stream-chat-react'
import { StreamChatGenerics } from '../../types'
import appColors from '../../../../appColors'
import chatNoFileSvg from '../../../../assets/svgs/chat-no-file.svg'
import { ReactComponent as DownloadIcon } from '../../../../assets/svgs/dowload-icon.svg'

interface FileItem {
  id: string
  name: string
  type: string
  size?: number
  url: string
  sharedBy: string
  sharedAt: Date
  messageId: string
  isImage: boolean
  isVideo: boolean
  thumbnail?: string
}

interface FilesTabProps {
  onClose: () => void
  searchResults?: any[]
  searchQuery?: string
  isSearchActive?: boolean
}

export const FilesTab: React.FC<FilesTabProps> = React.memo(
  ({ onClose, searchResults = [], searchQuery = '', isSearchActive = false }) => {
    const { channel } = useChannelStateContext<StreamChatGenerics>()
    const [files, setFiles] = useState<FileItem[]>([])
    const [images, setImages] = useState<FileItem[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      const loadChannelFiles = async () => {
        if (!channel) return

        try {
          setLoading(true)

          // Query messages with attachments
          const response = await channel.query({
            messages: { limit: 100 },
          })

          const allFiles: FileItem[] = []
          const allImages: FileItem[] = []

          response.messages?.forEach((message) => {
            if (message.attachments && message.attachments.length > 0) {
              message.attachments.forEach((attachment) => {
                const isImage = attachment.type === 'image'
                const isVideo = attachment.type === 'video'
                const isFile = attachment.type === 'file'

                if (isImage || isVideo || isFile) {
                  const fileItem: FileItem = {
                    id: `${message.id}-${attachment.type}`,
                    name: attachment.title || attachment.fallback || 'Unknown file',
                    type: attachment.mime_type || 'unknown',
                    size:
                      typeof attachment.file_size === 'string'
                        ? parseInt(attachment.file_size)
                        : attachment.file_size,
                    url: attachment.asset_url || attachment.image_url || '',
                    sharedBy: message.user?.name || 'Unknown',
                    sharedAt: new Date(message.created_at || Date.now()),
                    messageId: message.id || '',
                    isImage,
                    isVideo,
                    thumbnail: attachment.thumb_url || attachment.image_url,
                  }

                  if (isImage || isVideo) {
                    allImages.push(fileItem)
                  } else {
                    allFiles.push(fileItem)
                  }
                }
              })
            }
          })

          // Sort by date (newest first)
          allFiles.sort((a, b) => b.sharedAt.getTime() - a.sharedAt.getTime())
          allImages.sort((a, b) => b.sharedAt.getTime() - a.sharedAt.getTime())

          setFiles(allFiles)
          setImages(allImages)
        } catch (error) {
          console.error('Error loading channel files:', error)
        } finally {
          setLoading(false)
        }
      }

      loadChannelFiles()
    }, [channel])

    const formatFileSize = (bytes?: number): string => {
      if (!bytes) return ''

      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`
    }

    const formatDate = (date: Date): string => {
      const now = new Date()
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } else if (diffInHours < 24 * 7) {
        return date.toLocaleDateString([], { weekday: 'short' })
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
      }
    }

    const getFileIcon = (type: string): string => {
      if (type.includes('pdf')) return '📄'
      if (type.includes('word') || type.includes('document')) return '📝'
      if (type.includes('sheet') || type.includes('excel')) return '📊'
      if (type.includes('presentation') || type.includes('powerpoint')) return '📽️'
      if (type.includes('zip') || type.includes('archive')) return '🗜️'
      if (type.includes('audio')) return '🎵'
      return '📎'
    }

    const downloadFile = (file: FileItem) => {
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.name
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const highlightSearchText = (text: string, query: string) => {
      if (!query || !query.trim() || !text) return text

      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedQuery})`, 'gi')
      const parts = text.split(regex)

      return parts.map((part, index) => {
        if (regex.test(part)) {
          return (
            <span key={index} className="bg-app-orange-20 text-app-orange-100 rounded font-medium">
              {part}
            </span>
          )
        }
        return part
      })
    }

    // Convert search results to FileItem format
    const convertSearchResultsToFileItems = (
      results: any[]
    ): { files: FileItem[]; images: FileItem[] } => {
      const searchFiles: FileItem[] = []
      const searchImages: FileItem[] = []

      results.forEach((result) => {
        if (result.attachment) {
          const attachment = result.attachment
          const isImage = attachment.type === 'image'
          const isVideo = attachment.type === 'video'
          const isFile = attachment.type === 'file'

          if (isImage || isVideo || isFile) {
            const fileItem: FileItem = {
              id: `${result.message.id}-${attachment.type}`,
              name: attachment.title || attachment.fallback || 'Unknown file',
              type: attachment.mime_type || 'unknown',
              size:
                typeof attachment.file_size === 'string'
                  ? parseInt(attachment.file_size)
                  : attachment.file_size,
              url: attachment.asset_url || attachment.image_url || '',
              sharedBy: result.message.user?.name || 'Unknown',
              sharedAt: new Date(result.message.created_at || Date.now()),
              messageId: result.message.id || '',
              isImage,
              isVideo,
              thumbnail: attachment.thumb_url || attachment.image_url,
            }

            if (isImage || isVideo) {
              searchImages.push(fileItem)
            } else {
              searchFiles.push(fileItem)
            }
          }
        }
      })

      return { files: searchFiles, images: searchImages }
    }

    // Get the files and images to display (either search results or all files)
    const { files: displayFiles, images: displayImages } = isSearchActive
      ? convertSearchResultsToFileItems(searchResults)
      : { files, images }

    if (loading) {
      return (
        <div
          className="h-full flex items-center justify-center"
          style={{ backgroundColor: appColors['app-white']['100'] }}
        >
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: appColors['app-primary']['60'] }}
          ></div>
        </div>
      )
    }

    const totalFilesCount = displayImages.length + displayFiles.length

    return (
      <div
        className="h-full flex flex-col"
        style={{ backgroundColor: appColors['app-white']['100'] }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: appColors['app-grey']['25'] }}
        >
          <h3 className="text-lg font-semibold" style={{ color: appColors['app-black']['100'] }}>
            Files & Media
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-opacity-10"
            style={{ color: appColors['app-grey']['70'], backgroundColor: 'transparent' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {totalFilesCount === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="mb-6">
                <img src={chatNoFileSvg} alt="No files" className="w-32 h-32 mx-auto" />
              </div>
              <h4
                className="text-lg font-medium mb-2"
                style={{ color: appColors['app-black']['100'] }}
              >
                {isSearchActive ? 'No files found' : 'No files'}
              </h4>
              {isSearchActive && searchQuery && (
                <p className="text-sm" style={{ color: appColors['app-grey']['60'] }}>
                  No files match "{searchQuery}"
                </p>
              )}
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Search Results Header */}
              {isSearchActive && searchQuery && searchQuery.trim() && (
                <div className="px-4 py-2 bg-app-primary-10 border-b border-app-primary-20">
                  <p className="text-sm text-app-primary-100 font-medium">
                    Showing {totalFilesCount} result{totalFilesCount === 1 ? '' : 's'} for "
                    {searchQuery}"
                  </p>
                </div>
              )}

              {/* Pictures and Videos Section */}
              {displayImages.length > 0 && (
                <div>
                  <h4
                    className="text-sm font-medium mb-3"
                    style={{ color: appColors['app-grey']['70'] }}
                  >
                    Pictures and videos ({displayImages.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {displayImages.map((image) => (
                      <div
                        key={image.id}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity relative group"
                        onClick={() => window.open(image.url, '_blank')}
                      >
                        {/* Download icon overlay (top-right) */}
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            downloadFile(image)
                          }}
                          className="absolute top-2 right-2 bg-white/90 hover:bg-black/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all shadow-lg text-app-black-100 hover:text-white"
                          title="Download"
                          aria-label="Download"
                        >
                          <DownloadIcon className="w-4 h-4" />
                        </button>

                        {image.isVideo ? (
                          <div className="relative w-full h-full">
                            <video
                              src={image.url}
                              className="w-full h-full object-cover"
                              poster={image.thumbnail}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: appColors['app-white']['100'] }}
                              >
                                <svg
                                  className="w-4 h-4 ml-0.5"
                                  style={{ color: appColors['app-black']['100'] }}
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-full object-cover"
                            style={{ minWidth: '100%', minHeight: '100%' }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {displayFiles.length > 0 && (
                <div>
                  <h4
                    className="text-sm font-medium mb-3"
                    style={{ color: appColors['app-grey']['70'] }}
                  >
                    Documents ({displayFiles.length})
                  </h4>
                  <div className="space-y-2">
                    {displayFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center p-3 rounded-lg cursor-pointer hover:bg-opacity-50 transition-colors"
                        style={{ backgroundColor: 'transparent' }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = appColors['app-grey']['10'])
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'transparent')
                        }
                        onClick={() => downloadFile(file)}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                          style={{ backgroundColor: appColors['app-primary']['60'] }}
                        >
                          <span className="text-lg">{getFileIcon(file.type)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium truncate"
                            style={{ color: appColors['app-black']['100'] }}
                          >
                            {highlightSearchText(file.name, searchQuery)}
                          </p>
                          <p className="text-xs" style={{ color: appColors['app-grey']['60'] }}>
                            Shared by {file.sharedBy} on {formatDate(file.sharedAt)}
                            {file.size && ` • ${formatFileSize(file.size)}`}
                          </p>
                        </div>
                        <button
                          className="p-1 ml-2 rounded-full bg-app-grey-5 hover:bg-app-grey-20 transition-colors text-app-primary-60"
                          onClick={(e) => {
                            e.stopPropagation()
                            downloadFile(file)
                          }}
                          title="Download file"
                          aria-label="Download file"
                        >
                          <DownloadIcon className="w-5 h-5 text-app-primary-60" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)
